// AccountSettingsModal: Netflix-style account settings management
// Enhanced profile editing with preferences and account management

import React, { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { useAuth } from '@/hooks/useAuth'
import {
  User,
  Mail,
  Camera,
  Bell,
  Globe,
  Save,
  AlertCircle,
  CheckCircle,
  Upload
} from 'lucide-react'

interface UserProfileData {
  full_name: string
  email: string
  preferred_export_platform: string
  avatar_url: string | null
  notification_preferences: {
    email_marketing: boolean
    email_updates: boolean
    email_security: boolean
  }
}

interface AccountSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function AccountSettingsModal({
  isOpen,
  onClose
}: AccountSettingsModalProps) {
  const { user, signOut, updateProfile, loading } = useAuth()
  const [profileData, setProfileData] = useState<UserProfileData>({
    full_name: '',
    email: '',
    preferred_export_platform: 'shutterstock',
    avatar_url: null,
    notification_preferences: {
      email_marketing: false,
      email_updates: true,
      email_security: true
    }
  })

  const [isEditing, setIsEditing] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Initialize profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || '',
        preferred_export_platform: user.user_metadata?.preferred_export_platform || 'shutterstock',
        avatar_url: user.user_metadata?.avatar_url || null,
        notification_preferences: {
          email_marketing: user.user_metadata?.email_marketing ?? false,
          email_updates: user.user_metadata?.email_updates ?? true,
          email_security: user.user_metadata?.email_security ?? true
        }
      })
    }
  }, [user])

  // Auto-dismiss messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [message])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaveLoading(true)
    setMessage(null)

    try {
      const { error } = await updateProfile({
        full_name: profileData.full_name,
        preferred_export_platform: profileData.preferred_export_platform,
        email_marketing: profileData.notification_preferences.email_marketing,
        email_updates: profileData.notification_preferences.email_updates,
        email_security: profileData.notification_preferences.email_security
      })

      if (error) {
        throw new Error(error.message)
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' })
      setIsEditing(false)
    } catch (error) {
      console.error('Profile update error:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update profile'
      })
    } finally {
      setSaveLoading(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setProfileData({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || '',
        preferred_export_platform: user.user_metadata?.preferred_export_platform || 'shutterstock',
        avatar_url: user.user_metadata?.avatar_url || null,
        notification_preferences: {
          email_marketing: user.user_metadata?.email_marketing ?? false,
          email_updates: user.user_metadata?.email_updates ?? true,
          email_security: user.user_metadata?.email_security ?? true
        }
      })
    }
    setIsEditing(false)
    setMessage(null)
  }

  const exportPlatforms = [
    { id: 'shutterstock', name: 'Shutterstock' },
    { id: 'adobe-stock', name: 'Adobe Stock' },
    { id: 'istock', name: 'iStock by Getty Images' },
    { id: 'getty-images', name: 'Getty Images' },
    { id: 'generic', name: 'Generic Format' }
  ]

  const getUserInitials = () => {
    if (!profileData.full_name && !profileData.email) return 'U'
    const name = profileData.full_name || profileData.email
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Account Settings"
      subtitle="Profile, security, and preferences"
      maxWidth="xl"
    >
      <div className="p-6">
        {/* Success/Error Messages */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg border flex items-center ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-3" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-3" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          {/* Profile Picture Section */}
          <div className="flex items-center space-x-4 pb-6 border-b">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-lg font-medium">
              {profileData.avatar_url ? (
                <img
                  src={profileData.avatar_url}
                  alt="Profile"
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                getUserInitials()
              )}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Profile Picture</h3>
              <p className="text-sm text-gray-600 mb-2">Update your profile photo</p>
              <button
                type="button"
                className="flex items-center space-x-2 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-lg transition-colors"
                disabled={!isEditing}
              >
                <Upload className="w-4 h-4" />
                <span>Upload Photo</span>
              </button>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <User className="w-5 h-5 mr-2 text-gray-600" />
                Personal Information
              </h3>
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Edit
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  id="full_name"
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 pr-8"
                  />
                  <Mail className="absolute right-2 top-2.5 w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed here</p>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Globe className="w-5 h-5 mr-2 text-gray-600" />
              Preferences
            </h3>

            <div>
              <label htmlFor="preferred_platform" className="block text-sm font-medium text-gray-700 mb-1">
                Default Export Platform
              </label>
              <select
                id="preferred_platform"
                value={profileData.preferred_export_platform}
                onChange={(e) => setProfileData(prev => ({ ...prev, preferred_export_platform: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                {exportPlatforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-gray-600" />
              Notification Preferences
            </h3>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={profileData.notification_preferences.email_updates}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    notification_preferences: {
                      ...prev.notification_preferences,
                      email_updates: e.target.checked
                    }
                  }))}
                  disabled={!isEditing}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Product updates and feature announcements</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={profileData.notification_preferences.email_security}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    notification_preferences: {
                      ...prev.notification_preferences,
                      email_security: e.target.checked
                    }
                  }))}
                  disabled={!isEditing}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Security alerts and account changes</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={profileData.notification_preferences.email_marketing}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    notification_preferences: {
                      ...prev.notification_preferences,
                      email_marketing: e.target.checked
                    }
                  }))}
                  disabled={!isEditing}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Marketing emails and promotions</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveLoading || loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{saveLoading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  )
}

export default AccountSettingsModal